import styles from "./index.module.css";
import DocLayout from "../../components/DocLayout";
import { useLocation } from "react-router-dom";

const EULA = () => {
  // get url param ref
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const ref = searchParams.get("ref");

  return (
    <DocLayout callbackUrl={ref}>
      <div className={styles.container}>
        <h1>End-User License Agreement (EULA)</h1>
        <p>
          <strong>QR Studio LLC</strong>
        </p>
        <p>
          <strong>Last Updated:</strong> February 09, 2026
        </p>

        <h2>1. Introduction</h2>
        <p>
          This End-User License Agreement ("Agreement" or "EULA") is a legal
          agreement between you ("Licensee," "you," or "your") and QR Studio LLC
          (doing business as "QR Studio AI") ("QR Studio," "we," "us," or
          "our"), governing your use of QR Studio’s virtual reality (VR),
          augmented reality (AR), mixed reality (MR), desktop, mobile, web, and
          other interactive software applications, platforms, and services,
          including any related content, documentation, and updates
          (collectively, the "Software").
        </p>
        <p>
          By installing, accessing, or using the Software, you agree to be bound
          by this Agreement. If you do not agree, do not install, access, or use
          the Software.
        </p>
        <p>
          If you are using the Software on behalf of an organization (for
          example, a university, school, or enterprise), you represent and
          warrant that you have authority to bind that organization, and "you"
          includes that organization.
        </p>

        <h2>2. License Grant</h2>
        <p>
          Subject to payment of applicable fees (if any) and your compliance
          with this Agreement, QR Studio grants you a limited, non-exclusive,
          non-transferable, non-sublicensable license to install and use the
          Software:
        </p>
        <ul>
          <li>
            For the number of users, seats, or devices specified in your
            purchase order, license certificate, or applicable statement of
            work.
          </li>
          <li>
            For educational, training, and enterprise purposes consistent with
            the scope of use agreed upon in your applicable sales, support, or
            partnership agreement.
          </li>
        </ul>
        <p>Any rights not expressly granted are reserved by QR Studio.</p>

        <h2>3. Restrictions</h2>
        <p>You may not, and may not permit any third party to:</p>
        <ul>
          <li>
            Copy, modify, translate, adapt, or create derivative works of the
            Software, except as expressly permitted by QR Studio in writing.
          </li>
          <li>
            Reverse engineer, decompile, disassemble, or otherwise attempt to
            derive source code, algorithms, or underlying structure, except to
            the extent such restriction is prohibited by applicable law.
          </li>
          <li>
            Rent, lease, lend, sell, sublicense, distribute, publish, or
            otherwise transfer rights to the Software without QR Studio’s prior
            written consent.
          </li>
          <li>
            Remove, alter, or obscure any proprietary notices, trademarks, or
            labels.
          </li>
          <li>
            Use the Software for unlawful purposes, or to develop, train, or
            offer competitive products or services.
          </li>
          <li>
            Interfere with or circumvent any security, access control, or
            authentication mechanism.
          </li>
        </ul>

        <h2>4. Intellectual Property</h2>
        <p>
          All intellectual property rights in and to the Software, including all
          software, code, user interfaces, graphics, models, audio, video, text,
          documentation, and related materials, are owned exclusively by QR
          Studio and/or its licensors. This Agreement does not transfer any
          ownership rights.
        </p>
        <p>
          <strong>Feedback.</strong> If you provide suggestions, ideas, or
          feedback regarding the Software, QR Studio may use such feedback
          without restriction or compensation.
        </p>

        <h2>5. Third-Party Content, Services, and Hardware</h2>
        <p>
          The Software may include integrations, modules, SDKs, cloud services,
          or content provided by third parties ("Third-Party Services"). Your
          use of Third-Party Services is subject to their separate terms and
          conditions. QR Studio is not responsible for Third-Party Services,
          including their availability, security, or performance.
        </p>
        <p>
          The Software may require compatible VR/AR headsets, controllers,
          computers, mobile devices, operating systems, app stores, device
          management tools, or other hardware and platforms supplied by third
          parties. QR Studio does not warrant the operation, compatibility, or
          performance of third-party hardware or platforms. You are solely
          responsible for obtaining, maintaining, and ensuring proper
          functioning of all required hardware and platforms.
        </p>

        <h2>6. Updates and Support</h2>
        <p>
          QR Studio may, at its discretion, provide updates, patches, or
          enhancements to the Software. Updates may modify or remove features.
        </p>
        <p>
          Support and maintenance (if any) are provided only according to the
          terms of your support plan, purchase order, or enterprise agreement.
          QR Studio may discontinue features or services with reasonable notice,
          where practicable.
        </p>

        <h2>7. Data and Privacy</h2>
        <p>
          Use of the Software may involve collection, processing, or storage of
          limited user, device, performance, or analytics data, which may
          include training progress and usage metrics. QR Studio handles such
          data in accordance with its Privacy Policy and any applicable data
          processing terms agreed with your organization.
        </p>
        <p>
          If your organization is the customer, your organization is responsible
          for obtaining any required consents from end users, including
          students, employees, or other authorized users, as required by
          applicable law and institutional policies.
        </p>

        <h2>8. Fees and Payment</h2>
        <p>
          Your use of the Software may be contingent upon timely payment of
          applicable license, subscription, professional services, or
          maintenance fees as agreed in your purchase order or enterprise
          agreement. Non-payment may result in suspension or termination of
          access or the license.
        </p>

        <h2>9. Term and Termination</h2>
        <p>This Agreement remains in effect until terminated.</p>
        <p>
          QR Studio may terminate this Agreement immediately upon notice if you
          breach its terms. Upon termination, you must cease all use of the
          Software and destroy all copies in your possession or control.
        </p>
        <p>
          Sections relating to Intellectual Property, Restrictions, Warranty
          Disclaimer, Limitation of Liability, Indemnification, Governing Law
          and Dispute Resolution, Export Compliance, and other provisions that
          by their nature should survive, shall remain in effect.
        </p>

        <h2>10. Safety and No Professional Advice</h2>
        <p>
          The Software may be used for educational and training simulations,
          including simulations of medical, clinical, industrial, or
          safety-related procedures. The Software is provided for training and
          informational purposes only and does not provide medical, legal, or
          other professional advice. You are solely responsible for ensuring
          that training is supervised and that users follow applicable
          professional standards, institutional policies, and safety procedures.
        </p>
        <p>
          To the maximum extent permitted by law, QR Studio is not responsible
          for any injuries, incidents, or damages arising from the use of the
          Software or reliance on training simulations.
        </p>

        <h2>11. Warranty Disclaimer</h2>
        <p>
          THE SOFTWARE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES
          OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO
          WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE,
          TITLE, AND NON-INFRINGEMENT. QR STUDIO DOES NOT WARRANT THAT THE
          SOFTWARE WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE.
        </p>

        <h2>12. Limitation of Liability</h2>
        <p>
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, QR STUDIO SHALL NOT BE LIABLE
          FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR
          PUNITIVE DAMAGES, INCLUDING LOST PROFITS, LOST REVENUE, LOSS OF DATA,
          OR BUSINESS INTERRUPTION, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH
          DAMAGES.
        </p>
        <p>
          QR STUDIO’S TOTAL CUMULATIVE LIABILITY UNDER THIS AGREEMENT SHALL NOT
          EXCEED THE AMOUNT PAID BY YOU (OR YOUR ORGANIZATION) FOR THE SOFTWARE
          IN THE TWELVE (12) MONTHS PRECEDING THE EVENT GIVING RISE TO THE
          CLAIM.
        </p>

        <h2>13. Indemnification</h2>
        <p>
          You agree to indemnify, defend, and hold harmless QR Studio, its
          affiliates, officers, directors, employees, and licensors from and
          against any claims, damages, liabilities, costs, and expenses
          (including reasonable attorneys’ fees) arising from your violation of
          this Agreement, misuse of the Software, or violation of applicable
          law.
        </p>

        <h2>14. Governing Law & Dispute Resolution</h2>
        <p>
          This Agreement shall be governed by and construed in accordance with
          the laws of the State of Wyoming, without regard to conflict of laws
          principles.
        </p>
        <p>
          Any dispute, controversy, or claim arising out of or relating to this
          Agreement shall be resolved by binding arbitration administered by the
          American Arbitration Association ("AAA") in accordance with its
          Commercial Arbitration Rules then in effect. The arbitration shall be
          conducted in English by one arbitrator, unless the parties agree
          otherwise.
        </p>

        <h2>15. Export Compliance</h2>
        <p>
          You agree to comply with all applicable export control laws and
          regulations in your use of the Software, including U.S. export laws
          and sanctions regulations. You represent that you are not located in,
          under the control of, or a national or resident of any country or
          region subject to comprehensive sanctions, and that you are not a
          prohibited or restricted party under applicable export or sanctions
          laws.
        </p>

        <h2>16. Entire Agreement</h2>
        <p>
          This Agreement constitutes the entire agreement between you and QR
          Studio regarding the Software and supersedes all prior or
          contemporaneous agreements, understandings, or representations
          concerning the Software. If there is a conflict between this Agreement
          and a separate written agreement signed by QR Studio and your
          organization governing the Software, the signed agreement will control
          to the extent of the conflict.
        </p>

        <h2>Contact Information</h2>
        <address>
          QR Studio LLC
          <br />
          30 N Gould St Ste N
          <br />
          Sheridan, WY 82801
          <br />
          United States of America
          <br />
          <br />
          Website:{" "}
          <a
            href="https://qrstudio.ai"
            target="_blank"
            rel="noopener noreferrer"
          >
            qrstudio.ai
          </a>
          <br />
          Email: <a href="mailto:legal@qrstudio.ai">legal@qrstudio.ai</a>
        </address>
      </div>
      ```
    </DocLayout>
  );
};

export default EULA;
